import { watch } from 'vue'
import type { Ref } from 'vue'

import { useGraphChartData } from '@app/application/report/composables/use-graph-chart-data'
import type { GraphData } from '@app/application/report/screens/graphs-echarts/types'
import type { Locale } from '@app/shared/types/locale'

interface UsePinnedGraphChartReturn {
  graphData: Ref<GraphData | null>
  loading: Ref<boolean>
  errored: Ref<boolean>
}

/**
 * Loads the pinned-graph chart data and refetches when the pinned id changes.
 * Used by the drawer's `PinnedGraphCard` and the reports-index
 * `PinnedReportCard`. Wraps `useGraphChartData` with the standard fetch-on-id
 * watcher both call sites need.
 */
export function usePinnedGraphChart(
  pinnedId: Ref<number | null>,
  reportTemplateId: Ref<number>,
  locale: Ref<Locale>
): UsePinnedGraphChartReturn {
  const { graphData, loading, errored, fetchIfNeeded } = useGraphChartData(
    pinnedId,
    reportTemplateId,
    locale
  )

  // `immediate: true` triggers the initial fetch on mount when an id is set.
  watch(
    pinnedId,
    (id) => {
      if (id !== null) void fetchIfNeeded()
    },
    { immediate: true }
  )

  return { graphData, loading, errored }
}
